/**
 * Single source of truth for the compact deposit-meta label —
 * "USDT · 3 months · 3%" — used on every deposit-flavoured surface
 * (dashboard tiles, all-assets cards, target-deposit pickers in
 * Top-up / Close / Outflow, the deposit detail screen header).
 *
 * Renders only the parts that are present; missing fields are
 * silently dropped so legacy rows without a stored APY or term
 * stay readable. Re-uses the same `·` separator the rest of the
 * app already shows (chosen explicitly per the design note).
 *
 * `apyFraction` follows the canonical post-migration-00087 layout:
 * it's a fraction (0.03 = 3 %), NOT a percent. Callers that hold a
 * percent value should divide by 100 before passing.
 */
export const formatDepositMeta = (
  symbol: string | null | undefined,
  term: number | null | undefined,
  apyFraction: number | null | undefined,
): string => {
  const parts: string[] = [];
  if (symbol) {
    parts.push(symbol);
  }
  if (term != null && Number.isFinite(term) && term > 0) {
    parts.push(term === 1 ? '1 month' : `${term} months`);
  }
  if (apyFraction != null && Number.isFinite(apyFraction)) {
    // Strip trailing zeros so "3%" reads cleaner than "3.00%". The
    // toFixed(2) cap keeps two decimals when they're significant
    // (e.g. 1.25 % → "1.25%").
    const percent = (apyFraction * 100).toFixed(2).replace(/\.?0+$/, '');
    parts.push(`${percent}%`);
  }
  return parts.join(' · ');
};
